import maplibregl, { type PointLike, type PositionAnchor } from 'maplibre-gl';
import { useEffect, useEffectEvent, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useMap, useOverlays } from './mapContext';

interface Props {
  lng: number;
  lat: number;
  /** Colour of the default pin. Ignored when `className` is set. Read once. */
  color?: string;
  /** Use a custom <div> with this class instead of the default pin. Read once. */
  className?: string;
  /** Part of the element placed at the coordinates (default 'center'). Read once. */
  anchor?: PositionAnchor;
  /** Pixel offset from the coordinates. Read once. */
  offset?: PointLike;
  /** Click on the marker; the click does not reach the map. */
  onClick?: () => void;
  /** React content of the custom <div> (needs `className`), e.g. a label drawn on the map. */
  children?: ReactNode;
}

export function MapMarker({ lng, lat, color, className, anchor, offset, onClick, children }: Props) {
  const map = useMap();
  const overlays = useOverlays();
  const handleClick = useEffectEvent(() => onClick?.());

  // Subpixel, like MapPopup: MapLibre otherwise snaps markers to whole pixels when a move ends, a visible twitch.
  const [marker] = useState(() => {
    if (!className) return new maplibregl.Marker({ color, subpixelPositioning: true });
    const element = document.createElement('div');
    element.className = className;
    return new maplibregl.Marker({ element, anchor, offset, subpixelPositioning: true });
  });

  useEffect(() => {
    const el = marker.getElement();
    const listener = (e: MouseEvent) => {
      e.stopPropagation(); // don't trigger map clicks at the marker
      handleClick();
    };
    el.addEventListener('click', listener);
    return () => el.removeEventListener('click', listener);
  }, [marker]);

  useEffect(() => {
    if (!map) return;
    marker.setLngLat([lng, lat]).addTo(map);
  }, [map, marker, lng, lat]);

  useEffect(() => () => void marker.remove(), [marker]);

  useEffect(
    () =>
      overlays.register(() => {
        if (marker.getElement().isConnected) marker.setLngLat(marker.getLngLat());
      }),
    [overlays, marker],
  );

  return children && className ? createPortal(children, marker.getElement()) : null;
}
